/**
 * TableHoverActionsPlugin — "+" affordances at a table's bottom and right edges.
 * Hovering near the bottom edge shows a full-width button that appends a row;
 * hovering near the right edge shows a full-height button that appends a column.
 * Both append real rows/columns, so they persist through the Markdown round-trip.
 */
import { $getNearestNodeFromDOMNode } from 'lexical';
import {
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableRowNode,
} from '@lexical/table';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classes from './TableHoverActionsPlugin.module.scss';

const EDGE = 12; // px band near an edge that reveals the button

type Band = 'row' | 'column' | null;

export function TableHoverActionsPlugin({ anchorElem }: { anchorElem: HTMLElement }) {
  const [editor] = useLexicalComposerContext();
  const [band, setBand] = useState<Band>(null);
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const tableElemRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const table = target?.closest?.('table') as HTMLElement | null;
      // Keep the controls up while the cursor is on one of our buttons.
      if (!table) {
        if ((target as HTMLElement)?.closest?.(`.${classes.button}`)) return;
        setBand(null);
        return;
      }
      const tRect = table.getBoundingClientRect();
      const aRect = anchorElem.getBoundingClientRect();
      const nearBottom = e.clientY >= tRect.bottom - EDGE && e.clientY <= tRect.bottom + EDGE;
      const nearRight = e.clientX >= tRect.right - EDGE && e.clientX <= tRect.right + EDGE;
      tableElemRef.current = table;
      const base = {
        top: tRect.top - aRect.top,
        left: tRect.left - aRect.left,
        width: tRect.width,
        height: tRect.height,
      };
      if (nearRight) {
        setBand('column');
        setRect(base);
      } else if (nearBottom) {
        setBand('row');
        setRect(base);
      } else {
        setBand(null);
      }
    };
    anchorElem.addEventListener('mousemove', onMove);
    return () => anchorElem.removeEventListener('mousemove', onMove);
  }, [anchorElem]);

  const append = useCallback(
    (which: 'row' | 'column') => {
      const tableElem = tableElemRef.current;
      if (!tableElem) return;
      editor.update(() => {
        const node = $getNearestNodeFromDOMNode(tableElem);
        if (!node) return;
        const table = $getTableNodeFromLexicalNodeOrThrow(node);
        if (which === 'row') {
          const lastRow = table.getLastChild();
          if (!$isTableRowNode(lastRow)) return;
          const cell = lastRow.getLastChild();
          if ($isTableCellNode(cell)) {
            cell.selectEnd();
            $insertTableRowAtSelection(true);
          }
        } else {
          const firstRow = table.getFirstChild();
          if (!$isTableRowNode(firstRow)) return;
          const cell = firstRow.getLastChild();
          if ($isTableCellNode(cell)) {
            cell.selectEnd();
            $insertTableColumnAtSelection(true);
          }
        }
      });
    },
    [editor],
  );

  if (!band || !rect) return null;

  const style: React.CSSProperties =
    band === 'row'
      ? { top: rect.top + rect.height - 1, left: rect.left, width: rect.width, height: 18 }
      : { top: rect.top, left: rect.left + rect.width - 1, width: 18, height: rect.height };

  return createPortal(
    <button
      type="button"
      className={`${classes.button} ${band === 'row' ? classes.rowButton : classes.columnButton}`}
      data-testid={band === 'row' ? 'lexical-table-add-row' : 'lexical-table-add-column'}
      style={style}
      title={band === 'row' ? 'Add row' : 'Add column'}
      onClick={(e) => {
        e.stopPropagation();
        append(band);
      }}
    >
      +
    </button>,
    anchorElem,
  );
}
